import { useEffect, useState } from 'react';
import { api, type AutomationDocVersion, type DocEditMetrics } from '../../lib/api';
import { formatDate, timeAgo } from '../../lib/utils';

/**
 * People's edits to the Google Docs an automation files.
 *
 * When an automation saves each run's document to Drive and that folder is watched by the sources
 * hub, every edit somebody makes to the Doc comes back, is measured against what the automation
 * wrote, and only the changed paragraphs are filed into the knowledge base. These two views read
 * that record: the trend for the automation (a falling edit rate is the documents needing less
 * fixing), and the versions of one run's Doc.
 */

const percent = (share: number | null) => (share === null ? '—' : `${Math.round(share * 100)}%`);

/** The automation's edit rate, overall and week by week. Rendered under its stats when it files to Drive. */
export function DocEditTrend({ automationId }: { automationId: string }) {
  const [metrics, setMetrics] = useState<DocEditMetrics | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api.orgAutomations
      .editMetrics(automationId)
      .then((m) => live && setMetrics(m))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [automationId]);

  if (failed) return null;
  if (!metrics) return <p className="px-4 py-2.5 text-[11px] text-ink-faint">Loading edits…</p>;

  const { totals, weeks } = metrics;
  if (totals.filedToDrive === 0) return null;

  // Filed, but nothing has come back: the folder is not watched, so no edit could ever be seen — and
  // a rate of 0% would read as "nobody needed to fix anything".
  if (totals.tracked === 0) {
    return (
      <p className="px-4 py-2.5 text-[11px] text-ink-faint">
        Edits to this automation&apos;s Google Docs can&apos;t be seen: none of the {totals.filedToDrive} it filed in the last 12
        weeks has come back from the sources hub. Watch its Drive folder (Sources → Google Drive) to measure them.
      </p>
    );
  }

  const stats = [
    {
      label: 'Edited by people',
      value: `${totals.edited} of ${totals.tracked} · ${percent(totals.editRate)}`,
      title: 'Runs whose Google Doc someone edited, out of the runs whose Doc the sources hub has seen (last 12 weeks)',
    },
    {
      label: 'Changed on average',
      value: percent(totals.meanChangedShare),
      title: "How much of an edited document people changed in total, against what the automation wrote",
    },
    {
      label: 'Paragraphs removed',
      value: String(totals.paragraphsRemoved),
      title: 'Paragraphs people deleted — the clearest sign of what the automation should not have written',
    },
  ];
  const peak = Math.max(...weeks.map((w) => w.editRate ?? 0), 0.01);

  return (
    <div>
      <div className="grid grid-cols-3 divide-x divide-border-subtle">
        {stats.map((stat) => (
          <div key={stat.label} className="px-4 py-2.5" title={stat.title}>
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-ink-faint">{stat.label}</span>
            <span className="block mt-0.5 text-xs text-ink-secondary tabular-nums truncate">{stat.value}</span>
          </div>
        ))}
      </div>
      {weeks.length > 1 && (
        <div className="px-4 pb-2.5">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-ink-faint mb-1">
            Edit rate by week — should fall as the prompt improves
          </span>
          <div className="flex items-end gap-1 h-8" role="img" aria-label="Edit rate by week">
            {weeks.map((week) => (
              <div
                key={week.from}
                className="flex-1 min-w-[6px] rounded-sm bg-orange-400/80"
                style={{ height: `${Math.max(((week.editRate ?? 0) / peak) * 100, week.tracked > 0 ? 6 : 2)}%` }}
                title={`Week of ${week.from}: ${week.edited} of ${week.tracked} edited (${percent(week.editRate)})`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Every version of one run's Doc: the automation's original, then each person's edit. */
export function DocVersionList({ versions }: { versions: AutomationDocVersion[] }) {
  if (versions.length === 0) return null;
  const edits = versions.filter((v) => !v.byAutomation);

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-2">
        Edits in Google Docs {edits.length > 0 ? `(${edits.length})` : ''}
      </p>
      <ul className="space-y-1.5">
        {versions.map((v) => (
          <li key={v.id} className="flex flex-wrap items-baseline gap-x-2 text-xs" title={formatDate(v.modifiedTime)}>
            {v.byAutomation ? (
              <span className="text-ink-muted">Filed by the automation</span>
            ) : (
              <>
                <span className="text-ink-secondary">{v.editorName || v.editorEmail || 'Someone'}</span>
                <span className="text-ink-faint tabular-nums">
                  {v.paragraphsChanged} changed · {v.paragraphsAdded} added · {v.paragraphsRemoved} removed
                </span>
                <span className="text-ink-faint">· {percent(v.changedShare)} of the document so far</span>
                {!v.feedItemId && v.paragraphsChanged + v.paragraphsAdded === 0 && (
                  <span className="text-ink-faint">· nothing filed</span>
                )}
              </>
            )}
            <span className="ml-auto text-ink-faint">{timeAgo(v.modifiedTime)} ago</span>
          </li>
        ))}
      </ul>
      {edits.length === 0 && <p className="mt-1 text-[11px] text-ink-faint">Nobody has edited this document.</p>}
    </div>
  );
}
